// Composition Section — gender, employee type, work mode, tenure.
// Each tile is its own breakdown so they all update independently as
// page-level filters change.

import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import FemaleIcon from '@mui/icons-material/Female';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import HomeIcon from '@mui/icons-material/Home';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import type {
  HrDimension,
  HrEmployeeStatus,
  HrFilterMap,
  HrStatsBreakdownRow,
  HrStatsSummaryResponse,
} from '../../types/hrStats';
import { usePeopleBreakdown } from '../../hooks/usePeopleBreakdown';
import { formatNumberFull } from '../../utils/FormatUtils';
import SectionCard from './primitives/SectionCard';
import DonutBreakdown, {
  type DonutDatum,
} from './primitives/DonutBreakdown';
import { ACCENTS } from './primitives/peopleInsightsTokens';

export interface CompositionSectionProps {
  pageFilters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;
  /** Provided by useHrStats so we can re-use gender_ratio / tenure_buckets without an extra call. */
  summary?: HrStatsSummaryResponse;
  onApplyFilter: (dim: HrDimension, value: string) => void;
}

const GENDER_COLORS = [ACCENTS.indigo.main, ACCENTS.rose.main, ACCENTS.cyan.main];
const EMP_TYPE_COLORS = [ACCENTS.emerald.main, ACCENTS.amber.main, ACCENTS.violet.main];
const WORK_MODE_COLORS = [
  ACCENTS.indigo.main,
  ACCENTS.cyan.main,
  ACCENTS.emerald.main,
  ACCENTS.amber.main,
];

const CompositionSection: React.FC<CompositionSectionProps> = ({
  pageFilters,
  employeeStatus,
  summary,
  onApplyFilter,
}) => {
  const employeeTypeQuery = usePeopleBreakdown({
    dimension: 'employee_type',
    filters: pageFilters,
    employeeStatus,
  });
  const workModeQuery = usePeopleBreakdown({
    dimension: 'work_mode',
    filters: pageFilters,
    employeeStatus,
  });

  // We prefer gender from summary (already returned), but if the user changes
  // filters mid-flight summary may be stale; fall back to a dedicated query.
  const genderFromSummary: DonutDatum[] = useMemo(() => {
    if (!summary) return [];
    return summary.gender_ratio
      .filter((g) => g.count > 0)
      .map((g) => ({ name: g.label, value: g.count }));
  }, [summary]);

  const empTypeData: DonutDatum[] = (employeeTypeQuery.data?.rows ?? []).map((r) => ({
    name: r.label,
    value: r.count,
  }));

  const workModeRows = workModeQuery.data?.rows ?? [];
  const workModeTotal =
    workModeQuery.data?.total ?? workModeRows.reduce((s, r) => s + r.count, 0);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 2,
      }}
    >
      {/* Gender */}
      <SectionCard
        title="Gender Diversity"
        icon={
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              backgroundColor: ACCENTS.rose.surface,
              color: ACCENTS.rose.on,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FemaleIcon sx={{ fontSize: 16 }} />
          </Box>
        }
        bodyPadding={2.5}
      >
        {!summary ? (
          <Skeleton variant="rounded" height={170} />
        ) : genderFromSummary.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No gender data.
          </Typography>
        ) : (
          <DonutBreakdown
            data={genderFromSummary}
            colors={GENDER_COLORS}
            onSlice={(d) => onApplyFilter('gender', d.name)}
          />
        )}
      </SectionCard>

      {/* Employee Type */}
      <SectionCard
        title="Employee Type"
        icon={
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              backgroundColor: ACCENTS.cyan.surface,
              color: ACCENTS.cyan.on,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WorkOutlineIcon sx={{ fontSize: 16 }} />
          </Box>
        }
        bodyPadding={2.5}
      >
        {employeeTypeQuery.isLoading ? (
          <Skeleton variant="rounded" height={170} />
        ) : empTypeData.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No employee-type data.
          </Typography>
        ) : (
          <DonutBreakdown
            data={empTypeData}
            colors={EMP_TYPE_COLORS}
            onSlice={(d) => {
              const original = employeeTypeQuery.data?.rows.find((r) => r.label === d.name);
              if (original) onApplyFilter('employee_type', original.key);
            }}
          />
        )}
      </SectionCard>

      {/* Work mode */}
      <SectionCard
        title="Work Mode"
        icon={
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              backgroundColor: ACCENTS.emerald.surface,
              color: ACCENTS.emerald.on,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HomeIcon sx={{ fontSize: 16 }} />
          </Box>
        }
        bodyPadding={2.5}
      >
        {workModeQuery.isLoading ? (
          <Skeleton variant="rounded" height={170} />
        ) : workModeRows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No work-mode data.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            {workModeRows.map((w: HrStatsBreakdownRow, idx) => {
              const pct = workModeTotal > 0 ? (w.count / workModeTotal) * 100 : 0;
              return (
                <Box
                  key={w.key}
                  onClick={() => onApplyFilter('work_mode', w.key)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {w.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {pct.toFixed(1)}%
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      mt: 0.5,
                      height: 8,
                      borderRadius: 999,
                      backgroundColor: '#F1F5F9',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${pct}%`,
                        height: '100%',
                        backgroundColor:
                          WORK_MODE_COLORS[idx % WORK_MODE_COLORS.length],
                        transition: 'width 220ms ease',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
                  >
                    {formatNumberFull(w.count)} people
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      {/* Tenure */}
      <SectionCard
        title="Tenure Distribution"
        icon={
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              backgroundColor: ACCENTS.violet.surface,
              color: ACCENTS.violet.on,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HourglassEmptyIcon sx={{ fontSize: 16 }} />
          </Box>
        }
        bodyPadding={2.5}
      >
        {!summary ? (
          <Skeleton variant="rounded" height={170} />
        ) : summary.tenure_buckets.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No tenure data.
          </Typography>
        ) : (
          <Box sx={{ height: 170 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary.tenure_buckets} margin={{ top: 10 }}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis
                  dataKey="bucket"
                  tick={{ fontSize: 10, fill: '#64748B' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#64748B' }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <RechartsTooltip
                  cursor={{ fill: '#F8FAFC' }}
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #E2E8F0',
                    fontSize: 12,
                  }}
                  formatter={(value: number) => [formatNumberFull(value), 'People']}
                />
                <Bar
                  dataKey="count"
                  radius={[4, 4, 0, 0]}
                  fill={ACCENTS.violet.main}
                  onClick={(d: { bucket?: string }) => {
                    if (d?.bucket) onApplyFilter('tenure_bucket', d.bucket);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </SectionCard>
    </Box>
  );
};

export default CompositionSection;
